import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {colors} from 'shells/colors/colors';
import {EmptyStateSmall} from 'shells/empty-states/empty-state-small';
import {ErrorStateSmall} from 'shells/error-state/error-state-small';
// @ts-expect-error - imported from .js/.jsx file
import {LightbulbBrightIcon} from 'shells/icon/svg/lightbulb-bright-icon';
// @ts-expect-error - imported from .js/.jsx file
import {LoadingIcon} from 'shells/icon/svg/loading-icon';
import {Illustration} from 'shells/illustrations/illustration';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Link} from 'shells/link/link';
import {Body} from 'shells/typography/body';

import {LimitedType, AiFeatureType} from 'nutshell-graphql-types';
import type {ExternalServiceType} from 'nutshell-graphql-types';
import type {GetPotentialMeetings_potentialMeetings as Meeting} from 'nutshell-graphql-types';

import {useGetPotentialMeetings} from '../../../activity-ai/graphql/hooks/use-get-potential-meetings';
import {AiFeatureGateModal} from '../../../ai-home/ai-feature-gate-modal/ai-feature-gate-modal';
import {useGetSessionUser} from '../../../core/use-get-session-user';
import {
    getServiceNoun,
    getServiceTypeIcon,
    getServiceUrl,
    getIntegrationIsEnabled,
} from '../../../external-meetings/helpers';
import {useGetLimitsByType} from '../../../limits';
import {CallSummarizationFeatureGateModal} from '../../../limits/feature-gates/call-summarization-feature-gate-modal';

import {SummarizeSection} from './summarize-section';

import {MeetingCard, SummarizeCheckbox, TranscriptionSection} from '.';

import styles from './activity-log-modal.module.css';

interface Props {
    activityId: string;
    selectedMeeting: Meeting | null | undefined;
    setSelectedMeeting: (arg1?: Meeting | null | undefined) => void;
    shouldSummarize: boolean;
    setShouldSummarize: (arg1: boolean) => void;
    shouldTranscribe: boolean;
    setShouldTranscribe: (arg1: boolean) => void;
    isFeatureGateModalOpen: boolean;
    setIsFeatureGateModalOpen: (arg1: boolean) => void;
    serviceType: ExternalServiceType;
}

const getTitle = (serviceType: ExternalServiceType) => {
    return (
        <Body size='small' isBold={true}>
            {getServiceNoun(serviceType)}
        </Body>
    );
};

export function MeetingSection(props: Props) {
    const hasAiOutcomes = NutClientConfig.hasAiOutcomesFF;
    const limitsType = hasAiOutcomes ? LimitedType.AI_OUTCOMES : LimitedType.AI_SUMMARIES;
    const {limits, isLoading: limitsLoading} = useGetLimitsByType(limitsType);
    const {
        potentialMeetings,
        isLoading: potentialMeetingsLoading,
        errorMessage,
    } = useGetPotentialMeetings(props.activityId, props.serviceType);
    const {user} = useGetSessionUser();

    const serviceName = getServiceNoun(props.serviceType);
    const showUpsell = !getIntegrationIsEnabled(props.serviceType, user);

    let content;
    if (props.selectedMeeting) {
        const canTranscribe = props.selectedMeeting.recordingStatus !== 'NONE';
        const transcription = props.selectedMeeting.transcript;
        const transcriptionStatus = props.selectedMeeting.transcriptStatus;
        const summary =
            props.selectedMeeting.transcriptionSummary &&
            props.selectedMeeting.transcriptionSummary.summary;
        const summaryStatus =
            props.selectedMeeting.transcriptionSummary &&
            props.selectedMeeting.transcriptionSummary.status;

        const hasAvailableSummaries = (limits && limits.canAdd) || false;

        if (limitsLoading) {
            content = (
                <div className='flex justify-center'>
                    <LoadingIcon size={32} fill={colors.orange} />
                </div>
            );
        } else {
            content = (
                <div className='border br-3 pad-16'>
                    <StackLegacy spacing={16}>
                        <MeetingCard
                            meeting={props.selectedMeeting}
                            onClick={() => props.setSelectedMeeting(null)}
                            isSelected={true}
                        />
                        <StackLegacy spacing={8}>
                            {canTranscribe && hasAvailableSummaries && (
                                <StackLegacy spacing={8}>
                                    <SummarizeCheckbox
                                        shouldSummarize={props.shouldSummarize}
                                        setShouldSummarize={props.setShouldSummarize}
                                    />
                                    <SummarizeSection
                                        summary={summary}
                                        summaryStatus={summaryStatus}
                                        canTranscribe={canTranscribe}
                                    />
                                </StackLegacy>
                            )}
                            <TranscriptionSection
                                canTranscribe={canTranscribe}
                                shouldTranscribe={props.shouldTranscribe}
                                setShouldTranscribe={props.setShouldTranscribe}
                                hasAvailableSummaries={hasAvailableSummaries}
                                setIsFeatureGateModalOpen={props.setIsFeatureGateModalOpen}
                                transcription={transcription}
                                transcriptionStatus={transcriptionStatus}
                            />
                        </StackLegacy>
                    </StackLegacy>
                </div>
            );
        }
    } else if (potentialMeetingsLoading) {
        content = (
            <div className='flex justify-center'>
                <LoadingIcon size={32} fill={colors.orange} />
            </div>
        );
    } else if (errorMessage) {
        content = <ErrorStateSmall message={errorMessage} />;
    } else if (potentialMeetings.length > 0) {
        content = (
            <div className={styles['calls-container']}>
                {potentialMeetings.map((meeting: Meeting) => (
                    <MeetingCard
                        key={meeting.id}
                        meeting={meeting}
                        onClick={() => props.setSelectedMeeting(meeting)}
                        isSelected={false}
                    />
                ))}
            </div>
        );
    } else if (showUpsell) {
        content = (
            <div className='px-32 py-8'>
                <EmptyStateSmall
                    title={`${serviceName} integration is not enabled`}
                    titleSize='normal'
                    subtitle={
                        <Body size='small'>
                            It looks like this activity contains a {serviceName} link. Configure{' '}
                            <Link
                                // @ts-expect-error
                                as={RoutedLink}
                                variant='primary'
                                to={getServiceUrl(props.serviceType)}
                                size='small'
                            >
                                Nutshell&apos;s {serviceName} integration
                            </Link>{' '}
                            to automatically associate, transcribe, and summarize {serviceName}{' '}
                            meetings
                        </Body>
                    }
                    subtitleSize='small'
                    icon={getServiceTypeIcon(props.serviceType)}
                    isFlexibleHeight={true}
                    iconSpacing={24}
                />
            </div>
        );
    } else {
        content = (
            <div className='px-32 py-8'>
                <EmptyStateSmall
                    title={`No ${serviceName} meetings found`}
                    titleSize='normal'
                    subtitle={`Make sure you have a ${serviceName} integration correctly configured and meetings are being recorded`}
                    subtitleSize='small'
                    icon={<Illustration src='cactus-empty-state' width={80} />}
                    isFlexibleHeight={true}
                />
            </div>
        );
    }

    return (
        <>
            <StackLegacy spacing={8}>
                {props.selectedMeeting ||
                (showUpsell && potentialMeetings && potentialMeetings.length === 0) ? (
                    getTitle(props.serviceType)
                ) : (
                    <StackLegacy spacing={0}>
                        <StackLegacy isHorizontal={true} spacing={6}>
                            {getTitle(props.serviceType)}
                            <LightbulbBrightIcon size={11} />
                        </StackLegacy>
                        <Body size='mini'>
                            Your activity contains a {serviceName} link - select one below to
                            associate and transcribe it
                        </Body>
                    </StackLegacy>
                )}
                {content}
            </StackLegacy>
            {hasAiOutcomes ? (
                <AiFeatureGateModal
                    isOpen={props.isFeatureGateModalOpen}
                    onClose={() => props.setIsFeatureGateModalOpen(false)}
                    reason='outOfCredits'
                    aiFeatureType={AiFeatureType.GOOGLE_MEET_NOTETAKER}
                />
            ) : (
                <CallSummarizationFeatureGateModal
                    isOpen={props.isFeatureGateModalOpen}
                    onClose={() => props.setIsFeatureGateModalOpen(false)}
                />
            )}
        </>
    );
}
